import moment from 'moment';
import { Change } from '../../models';
import { requesterOf } from '../../utils/changeRequester';

export const OVERVIEW_COLUMN_IDS = [
    'slug',
    'title',
    'status',
    'project',
    'type',
    'assignee',
    'requestors',
    'estimate',
    'priority',
    'sprint',
    'createdAt',
    'updatedAt',
    'ticket',
] as const;

export type OverviewColumnId = typeof OVERVIEW_COLUMN_IDS[number];
export type SortDirection = 'asc' | 'desc';

export const DEFAULT_VISIBLE_COLUMNS: OverviewColumnId[] = [
    'slug',
    'title',
    'status',
    'project',
    'assignee',
    'requestors',
    'updatedAt',
];

export const COLUMNS_STORAGE_KEY = 'yoda.changesOverview.columns';

export function isOverviewColumnId(value: string): value is OverviewColumnId {
    return (OVERVIEW_COLUMN_IDS as readonly string[]).includes(value);
}

export function loadVisibleColumns(): OverviewColumnId[] {
    try {
        const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
        if (!raw) return [...DEFAULT_VISIBLE_COLUMNS];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [...DEFAULT_VISIBLE_COLUMNS];
        const valid = parsed.filter((item): item is OverviewColumnId => (
            typeof item === 'string' && isOverviewColumnId(item)
        ));
        return valid.length > 0 ? valid : [...DEFAULT_VISIBLE_COLUMNS];
    } catch {
        return [...DEFAULT_VISIBLE_COLUMNS];
    }
}

export function saveVisibleColumns(columns: OverviewColumnId[]): void {
    try {
        window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
    } catch {
        // ignore quota / private mode
    }
}

/** Map legacy sort query values to column + direction. */
export function parseSortParams(
    sortRaw: string | null,
    dirRaw: string | null,
): { sortBy: OverviewColumnId; sortDir: SortDirection } {
    const legacy: Record<string, { sortBy: OverviewColumnId; sortDir: SortDirection }> = {
        newest: { sortBy: 'createdAt', sortDir: 'desc' },
        oldest: { sortBy: 'createdAt', sortDir: 'asc' },
        status: { sortBy: 'status', sortDir: 'asc' },
        type: { sortBy: 'type', sortDir: 'asc' },
    };

    if (sortRaw && legacy[sortRaw]) {
        return legacy[sortRaw];
    }

    const sortBy = sortRaw && isOverviewColumnId(sortRaw) ? sortRaw : 'status';
    const sortDir: SortDirection = dirRaw === 'desc' ? 'desc' : 'asc';
    return { sortBy, sortDir };
}

function dateValue(value?: string | moment.Moment | null): number {
    if (!value) return 0;
    return moment(value).valueOf();
}

function latestSprintName(change: Change): string {
    const latest = change.sprints?.length ? change.sprints[change.sprints.length - 1] : null;
    return latest?.name || '';
}

function compareStrings(a: string, b: string): number {
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

export function compareChangesByColumn(
    a: Change,
    b: Change,
    column: OverviewColumnId,
    lang: string,
): number {
    switch (column) {
        case 'slug':
            return (a.slug || 0) - (b.slug || 0);
        case 'title':
            return compareStrings(
                a.localizedTitle(lang) || '',
                b.localizedTitle(lang) || '',
            );
        case 'status':
            return a.statusIndex() - b.statusIndex();
        case 'project':
            return compareStrings(a.project?.name || '', b.project?.name || '');
        case 'type':
            return compareStrings(a.type || '', b.type || '');
        case 'assignee':
            return compareStrings(
                a.assignee?.fullname?.() || a.assignee?.email || '',
                b.assignee?.fullname?.() || b.assignee?.email || '',
            );
        case 'requestors':
            return compareStrings(requesterOf(a), requesterOf(b));
        case 'estimate':
            return (a.estimate || 0) - (b.estimate || 0);
        case 'priority':
            return (a.priority || 0) - (b.priority || 0);
        case 'sprint':
            return compareStrings(latestSprintName(a), latestSprintName(b));
        case 'createdAt':
            return dateValue(a.createdAt) - dateValue(b.createdAt);
        case 'updatedAt':
            return dateValue(a.updatedAt) - dateValue(b.updatedAt);
        case 'ticket':
            return compareStrings(
                String(a.ticket_number || ''),
                String(b.ticket_number || ''),
            );
        default:
            return 0;
    }
}

export function sortChangesByColumn(
    items: Change[],
    sortBy: OverviewColumnId,
    sortDir: SortDirection,
    lang: string,
): Change[] {
    const sorted = [...items];
    const factor = sortDir === 'desc' ? -1 : 1;
    sorted.sort((a, b) => {
        const cmp = compareChangesByColumn(a, b, sortBy, lang);
        if (cmp !== 0) return cmp * factor;
        return (b.priority || 0) - (a.priority || 0);
    });
    return sorted;
}
